import { createMachine, transition, type SnapshotFrom } from "xstate";
import { METERS } from "@/data/tunables";
import type { GameState } from "@/state/GameState";

/**
 * M3 — the tick-cooldown machine (v0.10 RFC §3). The until-pattern:
 *
 *   ready ──FIRE──▶ lockedOut ──TICK[tick ≥ until]──▶ ready
 *
 * PURE per the Control Room contract: no actors, no timers, no clocks —
 * time is the sim tick carried ON the event; the transition function
 * only DESCRIBES (the engine applies effects to plain GameState). One
 * machine definition; today the BROKER is the only instance — a future
 * cooldown (e.g. the alumni contact boost) would differ only by
 * projector + duration (leg-1 S4: don't present-tense unbuilt work).
 */

export interface CooldownContext {
  /** Tick at which the lockout ends (0 = never locked). */
  until: number;
  /** Lockout length in ticks (1 tick = 1 s online). */
  durationTicks: number;
}

export type CooldownEvent = { type: "FIRE"; tick: number } | { type: "TICK"; tick: number };

export const cooldownMachine = createMachine({
  types: {} as { context: CooldownContext; events: CooldownEvent },
  id: "cooldown",
  context: { until: 0, durationTicks: 0 },
  initial: "ready",
  states: {
    ready: {
      on: {
        FIRE: {
          target: "lockedOut",
          // Executable action DESCRIBED, never run by xstate — the engine
          // owns all writes (projection model, RFC §2 call 2).
          actions: [{ type: "lock" }],
        },
      },
    },
    lockedOut: {
      on: {
        TICK: {
          target: "ready",
          guard: ({ context, event }) => event.tick >= context.until,
        },
        // FIRE while locked has NO transition — the machine refuses it,
        // and the caller fails loud with the remaining ticks.
      },
    },
  },
});

export type CooldownSnapshot = SnapshotFrom<typeof cooldownMachine>;

/** Project the BROKER's cooldown state from the plain save (no storage
 * beyond run.brokerLockoutUntil — SCHEMA 7's one new field). */
export function projectBrokerCooldown(state: GameState): CooldownSnapshot {
  return cooldownMachine.resolveState({
    value: state.run.tick >= state.run.brokerLockoutUntil ? "ready" : "lockedOut",
    context: {
      until: state.run.brokerLockoutUntil,
      durationTicks: METERS.brokerCooldownTicks,
    },
  });
}

/**
 * Ask the machine whether FIRE is allowed right now, and what the engine
 * must write if it is. Pure — the caller applies `lockUntil` (or throws
 * field-named using `ticksLeft`).
 */
export function fireBrokerCooldown(
  state: GameState,
): { allowed: true; lockUntil: number } | { allowed: false; ticksLeft: number } {
  const before = projectBrokerCooldown(state);
  const [after, actions] = transition(cooldownMachine, before, {
    type: "FIRE",
    tick: state.run.tick,
  });
  const locked = actions.some((a) => (a as { type?: string }).type === "lock");
  if (before.value === "ready" && after.value === "lockedOut" && locked) {
    // Duration comes from the PROJECTED context — the same number the
    // inspector shows (leg-1 S5: context drives logic, not just display).
    return { allowed: true, lockUntil: state.run.tick + (before.context as { durationTicks: number }).durationTicks };
  }
  return { allowed: false, ticksLeft: Math.max(0, state.run.brokerLockoutUntil - state.run.tick) };
}
